import { ApiResponse, CreatePaymentData, Lead, ListSort } from '../models';
import * as http from './http';
import ApiService from './service';
import {
  Dict,
  PaginatedApiResponse,
  RefundApprovelRequestData,
  MediaFile,
  SendPaymentLinkData,
  PaymentMethods,
} from '../..';
import {
  Transaction,
  TransactionList,
  PaymentResponse,
  CreateTransferPayment,
  PaymentStatus,
} from '../models/configuration';

export class PaymentService extends ApiService {
  async collectPayment(
    data: CreatePaymentData & { leadId: string; configId: string }
  ) {
    let response;
    if (
      ![
        PaymentMethods.CreditCard,
        // PaymentMethods.AmericanExpress,
        // PaymentMethods.KNet,
        // PaymentMethods.WebPayment,
      ].includes(data.paymentMethod)
    ) {
      if (data.transactionAddedAgainst !== 'customer') {
        const url = `${this.apiDomain}/api/payment/entity/payment-request/${data.configId}/`;
        const newData = {
          leadId: data.leadId,
          configId: data.configId,
          amount: data.amount,
          completedAt: new Date(),
          status: PaymentStatus.Paid,
          isRefunded: false,
          isTransferred: false,
          showroomId: data.showroomId,
          requestAddedAgainst: data.transactionAddedAgainst,
          paymentMethod: data.paymentMethod,
          paidBy: data.paidBy,
          skipEmailAndPhoneValidation: true,
          card: data.card,
          cashPaymentDetails: data.cashPaymentDetails,
          chequeDetails: data.chequeDetails,
          bankTransferDetails: data.bankTransferDetails,
        };
        response = await http.post<ApiResponse<PaymentResponse>>(
          url,
          newData,
          this.store
        );
      } else {
        const url = `${this.apiDomain}/api/payment/entity/payment-request/${data.customerId}/`;
        const newData = {
          amount: data.amount,
          completedAt: new Date(),
          status: PaymentStatus.Paid,
          isRefunded: false,
          isTransferred: false,
          showroomId: data.showroomId,
          requestAddedAgainst: data.transactionAddedAgainst,
          paymentMethod: data.paymentMethod,
          paidBy: data.paidBy,
          skipEmailAndPhoneValidation: true,
          card: data.card,
          cashPaymentDetails: data.cashPaymentDetails,
          chequeDetails: data.chequeDetails,
          bankTransferDetails: data.bankTransferDetails,
        };
        response = await http.post<ApiResponse<PaymentResponse>>(
          url,
          newData,
          this.store
        );
      }
    } else {
      const url = `${this.apiDomain}/api/payment`;
      response = await http.post<ApiResponse<PaymentResponse>>(
        url,
        data,
        this.store
      );
    }
    return response.data?.data;
  }
  async downloadReceipt(paymentId: string) {
    const url = `${this.apiDomain}/api/payment/${paymentId}/download/pdf`;
    const reponse = await http.download(url, this.store);
    http.saveDownloadedFile(reponse.data, 'application/pdf', `Payment.pdf`);
    return;
  }
  async receivePayment(paymentId: string, forPaymentRequest: boolean = false) {
    const url = `${this.apiDomain}/api/payment/${paymentId}/receive`;
    const response = await http.put<ApiResponse<Lead>>(
      url,
      { forPaymentRequest },
      this.store
    );
    return response.data;
  }
  async getTransactions(
    data: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<TransactionList> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/payment/list`;

    const resposne = await http.post<any>(url, data, this.store);

    return {
      ...resposne.data.data,
      lastUpdatedAt: new Date(),
      collectionSummary: resposne.data.data?.collectionSummary,
    };
  }

  async getAllTransactions(
    data: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Transaction> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/payment/list/customer-transactions`;

    const resposne = await http.post<any>(url, data, this.store);

    return {
      ...resposne.data.data,
      lastUpdatedAt: new Date(),
      collectionSummary: resposne.data.data?.collectionSummary,
    };
  }

  async getAllRequest(
    data: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Transaction> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/payment/list/customer-payment-requests`;

    const resposne = await http.post<any>(url, data, this.store);

    return {
      ...resposne.data.data,
      lastUpdatedAt: new Date(),
      collectionSummary: resposne.data.data?.collectionSummary,
    };
  }

  public async downloadPayments(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/payment/list/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Customers-${new Date()}.xlsx`
    );
  }

  public async downloadAllPayments(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/payment/list/customer-transactions/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Transactions-${new Date()}.xlsx`
    );
  }

  public async downloadAllRequests(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/payment/list/customer-payment-requests/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Transactions-Requests-${new Date()}.xlsx`
    );
  }

  async refundPayment(
    data: CreatePaymentData & { leadId: string; configId: string }
  ) {
    let response;
    if (
      ![
        PaymentMethods.CreditCard,
        // PaymentMethods.AmericanExpress,
        // PaymentMethods.KNet,
        // PaymentMethods.WebPayment,
      ].includes(data.paymentMethod)
    ) {
      if (data.transactionAddedAgainst !== 'customer') {
        const url = `${this.apiDomain}/api/payment/refund/payment-request-config/${data.configId}/`;
        const newData = {
          leadId: data.leadId,
          configId: data.configId,
          amount: data.amount,
          completedAt: new Date(),
          status: PaymentStatus.Paid,
          isRefunded: false,
          isTransferred: false,
          showroomId: data.showroomId,
          requestAddedAgainst: data.transactionAddedAgainst,
          paymentMethod: data.paymentMethod,
          paidBy: data.paidBy,
          skipEmailAndPhoneValidation: true,
          card: data.card,
          cashPaymentDetails: data.cashPaymentDetails,
          chequeDetails: data.chequeDetails,
          bankTransferDetails: data.bankTransferDetails,
          comments: data.comments,
          refundedTransactionIds: data.refundedTransactionIds,
        };
        response = await http.post<ApiResponse<PaymentResponse>>(
          url,
          newData,
          this.store
        );
      } else {
        const url = `${this.apiDomain}/api/payment/refund/payment-request/${data.customerId}/`;
        const newData = {
          amount: data.amount,
          completedAt: new Date(),
          status: PaymentStatus.Paid,
          isRefunded: false,
          isTransferred: false,
          showroomId: data.showroomId,
          requestAddedAgainst: data.transactionAddedAgainst,
          paymentMethod: data.paymentMethod,
          paidBy: data.paidBy,
          skipEmailAndPhoneValidation: true,
          card: data.card,
          cashPaymentDetails: data.cashPaymentDetails,
          chequeDetails: data.chequeDetails,
          bankTransferDetails: data.bankTransferDetails,
          comments: data.comments,
          customerId: data.customerId,
          refundedTransactionIds: data.refundedTransactionIds,
        };
        response = await http.post<ApiResponse<PaymentResponse>>(
          url,
          newData,
          this.store
        );
      }
    } else {
      const url = `${this.apiDomain}/api/payment/refund`;
      response = await http.post<ApiResponse<PaymentResponse>>(
        url,
        data,
        this.store
      );
    }

    return response.data?.data;
  }
  async refundToCustomer(
    data: CreatePaymentData & { leadId: string; configId: string }
  ) {
    let response;
    if (
      ![
        PaymentMethods.CreditCard,
        // PaymentMethods.AmericanExpress,
        // PaymentMethods.KNet,
        // PaymentMethods.WebPayment,
      ].includes(data.paymentMethod)
    ) {
      const url = `${this.apiDomain}/api/payment/refund/payment-request/${data.customerId}/`;
      const newData = {
        amount: data.amount,
        completedAt: new Date(),
        status: PaymentStatus.Paid,
        isRefunded: false,
        isTransferred: false,
        showroomId: data.showroomId,
        requestAddedAgainst: data.transactionAddedAgainst,
        paymentMethod: data.paymentMethod,
        paidBy: data.paidBy,
        skipEmailAndPhoneValidation: true,
        card: data.card,
        cashPaymentDetails: data.cashPaymentDetails,
        chequeDetails: data.chequeDetails,
        bankTransferDetails: data.bankTransferDetails,
        comments: data.comments,
        customerId: data.customerId,
        refundedTransactionIds: data.refundedTransactionIds,
      };
      response = await http.post<ApiResponse<PaymentResponse>>(
        url,
        newData,
        this.store
      );
    } else {
      const url = `${this.apiDomain}/api/payment/refund/customer`;
      response = await http.post<ApiResponse<PaymentResponse>>(
        url,
        data,
        this.store
      );
    }

    return response.data?.data;
  }
  async refundApprove(data: RefundApprovelRequestData, requestId: string) {
    const url = `${this.apiDomain}/api/payment/${requestId}/approve`;
    const response = await http.put<ApiResponse<PaymentResponse>>(
      url,
      data,
      this.store
    );
    return response.data?.data;
  }
  async refundReject(data: RefundApprovelRequestData, requestId: string) {
    const url = `${this.apiDomain}/api/payment/${requestId}/reject`;
    const response = await http.put<ApiResponse<PaymentResponse>>(
      url,
      data,
      this.store
    );
    return response.data?.data;
  }
  async uploadReceipt(data: MediaFile, requestId: string) {
    const url = `${this.apiDomain}/api/payment/${requestId}/signed-receipt`;
    const response = await http.put<ApiResponse<PaymentResponse>>(
      url,
      data,
      this.store
    );
    return response.data?.data;
  }
  async sendPaymentLink(data: SendPaymentLinkData, configId: string) {
    const url = `${this.apiDomain}/api/payment/entity/${configId}/payment-request-link`;
    const response = await http.post<ApiResponse<PaymentResponse>>(
      url,
      data,
      this.store
    );
    return response.data?.data;
  }
  async resendPaymentLink(
    paymentRequestId: string,
    configId: string,
    transactionAddedAgainst: string
  ) {
    const url = `${this.apiDomain}/api/payment/payment-request/resend`;
    const response = await http.post<ApiResponse<PaymentResponse>>(
      url,
      { paymentRequestId, configId, transactionAddedAgainst },
      this.store
    );
    return response.data?.data;
  }
  async removePaymentLink(paymentRequestId: string) {
    const url = `${this.apiDomain}/api/payment/payment-request/${paymentRequestId}`;
    const response = await http.remove<ApiResponse<PaymentResponse>>(
      url,
      this.store
    );
    return response.data?.data;
  }
  async transferToCustomer(data: CreateTransferPayment, configId: string) {
    const url = `${this.apiDomain}/api/payment/transfer-to-customer`;
    const response = await http.post<ApiResponse<PaymentResponse>>(
      url,
      data,
      this.store
    );
    return response.data?.data;
  }
  async transferToConfiguration(data: CreateTransferPayment, configId: string) {
    const url = `${this.apiDomain}/api/payment/transfer-to-configuration`;
    const response = await http.post<ApiResponse<PaymentResponse>>(
      url,
      data,
      this.store
    );
    return response.data?.data;
  }

  public async syncPayment(id: string): Promise<PaymentResponse[]> {
    const url = `${this.apiDomain}/api/payment/${id}/send-to-one-drive`;

    const resposne = await http.put<ApiResponse<PaymentResponse[]>>(
      url,
      {},
      this.store
    );
    return resposne.data?.data;
  }

  async getAllRequests(
    data: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Transaction> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/payment/list/all-payment-requests`;

    const resposne = await http.post<any>(url, data, this.store);

    return {
      ...resposne.data.data,
      lastUpdatedAt: new Date(),
      collectionSummary: resposne.data.data?.collectionSummary,
    };
  }

  public async downloadAllPaymentRequests360(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/payment/list/all-payment-requests/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Transaction-Requests-${new Date()}.xlsx`
    );
  }

  public async paymentMkPending(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
      applyVisibilityFilter?: boolean;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Transaction> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/payment/mk/pending`;

    const resposne = await http.post<PaginatedApiResponse<Transaction>>(
      url,
      data,
      this.store
    );

    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async downloadpaymentMkPending(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/payment/mk/pending/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Payment-${new Date()}.xlsx`
    );
  }
}
